/**
 * Returns true if Fullstory is installed correctly.
 */ function doesFullStoryExist() {
    return !!window[window['_fs_namespace']];
}
/**
 * Get the project ID from a Sentry DSN
 * @param {string} dsn
 */ const getProjectIdFromSentryDsn = (dsn)=>{
    return new URL(dsn).pathname.replace('/', '');
};
const isError = (exception)=>{
    return exception.message !== undefined;
};
/**
 * Get the message and name properties from the original exception
 * @param {EventHint} hint
 */ const getOriginalExceptionProperties = (hint)=>{
    if (hint && hint.originalException && isError(hint.originalException)) {
        const originalException = hint.originalException;
        const { name, message } = originalException;
        return {
            name,
            message
        };
    }
    return {};
};
/**
 * Returns the sentry URL of the error. If we cannot get the URL, return a
 * string saying we cannot.
 */ function getSentryUrl({ hint, sentryOrg, baseSentryUrl, client }) {
    try {
        // No docs on this but the SDK team assures me it works unless you bind another Sentry client
        const { dsn } = client.getOptions();
        if (!dsn) {
            console.error('No sn');
            return 'Could not retrieve url';
        }
        if (!hint) {
            console.error('No event hint');
            return 'Could not retrieve url';
        }
        const projectId = getProjectIdFromSentryDsn(dsn);
        return `${baseSentryUrl}/organizations/${sentryOrg}/issues/?project=${projectId}&query=${hint.event_id}`;
    } catch (err) {
        console.error('Error retrieving project ID from DSN', err);
        //TODO: Could put link to a help here
        return 'Could not retrieve url';
    }
}
function getFullStoryUrl(fullStoryClient) {
    // getCurrentSessionURL isn't available until after the FullStory script is fully bootstrapped.
    // If an error occurs before getCurrentSessionURL is ready, make a note in Sentry and move on.
    // More on getCurrentSessionURL here: https://help.fullstory.com/develop-js/getcurrentsessionurl
    try {
        const res = fullStoryClient.getCurrentSessionURL == null ? void 0 : fullStoryClient.getCurrentSessionURL.call(fullStoryClient, true);
        if (!res) {
            throw new Error('No FullStory session URL found');
        }
        return res;
    } catch (e) {
        const reason = e instanceof Error ? e.message : String(e);
        throw new Error(`Unable to get url: ${reason}`);
    }
}

const INTEGRATION_NAME = 'SentryFullStory';
function fullStoryIntegration(sentryOrg, options) {
    const fullStoryClient = options.client;
    const baseSentryUrl = options.baseSentryUrl || 'https://sentry.io';
    let fullStoryUrl;
    return {
        name: INTEGRATION_NAME,
        async processEvent (event, hint, client) {
            const self = client.getIntegrationByName(INTEGRATION_NAME);
            // Run the integration ONLY when it was installed on the current Hub AND isn't a transaction
            if (self && event.type === undefined && doesFullStoryExist()) {
                if (!fullStoryUrl) {
                    try {
                        fullStoryUrl = await getFullStoryUrl(fullStoryClient);
                    } catch (e) {
                        const reason = e instanceof Error ? e.message : String(e);
                        console.error(`Unable to get FullStory session URL: ${reason}`);
                    }
                }
                if (fullStoryUrl) {
                    event.contexts = {
                        ...event.contexts,
                        fullStory: {
                            fullStoryUrl
                        }
                    };
                }
                try {
                    fullStoryClient.event('Sentry Error', {
                        sentryUrl: getSentryUrl({
                            baseSentryUrl: baseSentryUrl,
                            sentryOrg,
                            hint,
                            client
                        }),
                        ...getOriginalExceptionProperties(hint)
                    });
                } catch (e) {
                    console.debug('Unable to report sentry error details to FullStory');
                }
            }
            return event;
        }
    };
}

export { fullStoryIntegration };
